import * as React from 'react';
import { Box, Typography } from '@material-ui/core';
import cn from 'classnames';
import { useFormik } from 'formik';
import { Redirect } from 'react-router';
import RenderText from '../../dictionaries/RenderText';
import FormikFormattedTextField from '../../components/FormikInputWrappers/FormikFormattedTextField';
import { getFormikValidator } from '../../utils/getFormikValidator';
import Btn from '../../components/Btn/Btn';
import { NoInformationSVG } from '../../assets/icons/NoInformationSVG';
import { IdentificationConfirmationStyles } from './IdentificationConfirmationStyles';
import ApprovedByBankSVG from '../../assets/images/NegotiationState/ApprovedByBankSVG';
import { useUpdatePhoneMutation } from '../../generated/graphql';
import { LoaderDialog } from '../../components/LoaderDialog/LoaderDialog';
import { useGetProfileAndDebtsReport } from '../../utils/useGetProfileAndDebtsReport';
import { useBankContext } from '../../dictionaries/context/BankContext';
import { storage } from '../../utils/storage';
import { config } from '../../config';

interface IDConfirmationNoInformationProps {
  id?: string | number;
  baseClasses: IdentificationConfirmationStyles;
  hasIdentityOptions: boolean;
}

const initialValues = { phone: '' };

const IdentificationConfirmationNoInformation:
React.FC<IDConfirmationNoInformationProps> = (props) => {
  const { baseClasses, id, hasIdentityOptions } = props;

  const [ updatePhone, result ] = useUpdatePhoneMutation({
    onError: (error) => {
      let message = 'Ocurrió un error en el proceso.';
      if (error.graphQLErrors[0].message === 'Invalid phone') message = 'Número inválido';
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      formik.setErrors({ phone: message });
    },
  });

  const formik = useFormik({
    initialValues,
    validate: getFormikValidator({
      phone: 'required|digits:10|regex:/^(?!57)3/',
    }, undefined, undefined, {
      required: 'Este campo es obligatorio',
      digits: 'El teléfono celular debe tener 10 dígitos',
      regex: 'Número inválido, debe iniciar con 3 y no debes incluir el código de país (57)',
    }),
    onSubmit: ({ phone }) => {
      updatePhone({ variables: { phone } });
    },
  });

  const {
    outDebtsReportData, outDebtsReportLoading, profileData,
  } = useGetProfileAndDebtsReport();

  // remove bank if no info
  const [ bank, setBank ] = useBankContext();
  React.useEffect(() => {
    if (!bank) return;
    storage.removeItem(config.bankKey);
    setBank(undefined);
  }, [ bank, setBank ]);

  const hasPhone = profileData?.profile?.phone;
  const descriptionKey = hasPhone
    ? 'validation_no_information_description_with_phone' : 'validation_no_information_description';

  if (!outDebtsReportLoading && outDebtsReportData?.outDebtsReport.hasSome && hasIdentityOptions) {
    return <Redirect to="/" />;
  }

  if (result.data?.updatePhone) {
    return (
      <>
        <Box style={{ textAlign: 'center', margin: 'auto' }}>
          <Box style={{ margin: '40px auto' }}>
            <ApprovedByBankSVG />
          </Box>
          <Typography className={cn(baseClasses.title, 'mb-2')}>
            <RenderText textKey="validation_information_added_title" />
          </Typography>
          <Typography className={cn(baseClasses.text, 'mt-2 mb-3')}>
            <RenderText textKey="validation_information_added_description" />
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <LoaderDialog open={result.loading} />

      <Box style={{ margin: '40px auto' }}>
        <NoInformationSVG />
      </Box>
      <Typography className={cn(baseClasses.title, 'mb-2')}>
        <RenderText textKey="validation_no_information_title" />
      </Typography>

      <Typography className={cn(baseClasses.text, 'mt-2 mb-3')}>
        <RenderText isMarkdown textKey="validation_options_document" context={{ document: id || '' }} />
      </Typography>

      <Typography className={cn(baseClasses.text, 'mt-2 mb-3')}>
        <RenderText textKey={descriptionKey} />
      </Typography>
      {!hasPhone && (
        <form onSubmit={formik.handleSubmit}>
          <FormikFormattedTextField
            label="Número de celular:"
            valueId="phone"
            formik={formik}
            inputProps={{
              startAdornment: <Typography color="textSecondary">+57</Typography>,
            }}
            data-test="cy-phoneValidation-input"
            phone
            placeholder="300 123 1234"
            fullWidth
            disableAutoComplete
          />
          <Box display="flex" justifyContent="space-between" mt={4}>
            <Btn color="primary" type="submit" disabled={!formik.values.phone} className="ml-auto">
              <RenderText textKey="validation_tc_next_button" />
            </Btn>
          </Box>
        </form>
      )}
    </>
  );
};

export default IdentificationConfirmationNoInformation;
